<script setup lang="ts">
import { lock, unlock, clearBodyLocks } from 'tua-body-scroll-lock'
import { YSC_API_SEARCH_ERROR, YSC_API_WISHLIST_ERROR } from '~/utils/error/YscErrorClasses'

defineEmits<{
  wishlist: [number]
}>()

const { map: vehicleTypeMap } = useReferentialMap('vehicle_type_as_int')

const formatMoreThan = useFormatMoreThan()

const route = useRoute()
const { t } = useI18n()

const {
  renderMap,
  loading,
  total,
  page,
  pageSize,
  results,
  currentFilter,
  filters,
  markedItemId,
  hoveredItemId,
  isCrossedResults,
} = storeToRefs(useSearchStore())
const { searchCampers } = useSearchStore()

const { isLoggedIn } = storeToRefs(useUserStore())

const { fetchWishlists, isCamperInWishlist } = useWishlistsStore()

const formMap = ref({
  radius: 0,
  latitude: 0,
  longitude: 0,
})

const showMap = ref(false)
// Key to force re-render the map on toggle map for mobile TT-10406
const mapKey = ref(0)
// locally keep information that user has hidden the map ; avoid map to render on page change (see route watcher)
const mapHiddenByUser = ref(false)

const onMarkerClick = (camper: { id: number }) => {
  markedItemId.value = camper.id
}

const { navigateToFilters } = useSearchNavigateToFilters()
const searchInArea = async () => {
  filters.value = {
    ...filters.value,
    ...formMap.value,
  }
  await navigateToFilters()
}

const resetFilters = async () => {
  const initial = initialSearchFiltersFactory()

  const query = route.query

  filters.value = {
    ...initial,
    where: queryValueToString(query.where),
    latitude: queryValueToNumber(query.latitude),
    longitude: queryValueToNumber(query.longitude),
    date_from: queryValueToString(query.date_from),
    date_to: queryValueToString(query.date_to),
    radius: null,
  }

  await navigateToFilters()
}

const increaseSearchRadius = async () => {
  if (filters.value.radius) {
    filters.value.radius = filters.value.radius * 2

    await navigateToFilters()
  }
}

const shouldBlockInterface = computed(() => loading.value || !!currentFilter.value)

watch(shouldBlockInterface, (value) => {
  if (!resultsElement.value) {
    return
  }

  if (value) {
    lock(resultsElement.value)
  }
  else {
    unlock(resultsElement.value)
  }
})

onBeforeUnmount(() => {
  clearBodyLocks()
})

const vehicleTypesForTitle = computed(() => {
  const { types = null } = route.query

  const vehicleTypes = queryValueToArray(types)

  if (!vehicleTypes || vehicleTypes.length !== 1 || vehicleTypes[0] === null) {
    return t('components.app_search.title')
  }

  const vehicleTypeAsInt = parseInt(vehicleTypes[0])

  // @ts-expect-error ts throwing a error because vehicleTypeAsInt is not the actual enum of number, but it's fine we handle if it's outside of it.
  const vehicleType: string | null = vehicleTypeMap.value[vehicleTypeAsInt]?.key.toLowerCase() || null

  if (!vehicleType) {
    return t('components.app_search.title')
  }

  const typeMap: Record<string, string> = {
    aclass: t('commons.rent_a_camper.aclass'),
    campervan: t('commons.rent_a_camper.campervan'),
    caravan: t('commons.rent_a_camper.caravan'),
    coachbuilt: t('commons.rent_a_camper.coachbuilt'),
    lowprofile: t('commons.rent_a_camper.lowprofile'),
    other: t('commons.rent_a_camper.other'),
    van: t('commons.rent_a_camper.van'),
  }
  return typeMap[vehicleType]
})

const resultsElement = ref<HTMLElement>()

const scrollToTop = () => {
  if (!import.meta.client) {
    return
  }

  window.scrollTo({ top: 0, behavior: 'smooth' })
  if (resultsElement.value) {
    resultsElement.value.scrollTop = 0
  }
}

watch(() => route.query.page, (value) => {
  page.value = queryValueToNumber(value) ?? 1
}, { immediate: true })

const { $errorManager } = useErrorManager()
const { execute: executeSearch } = await useLazyAsyncData(async () => {
  const [wishlistRequest, camperRequest] = await Promise.allSettled([
    fetchWishlists(),
    searchCampers(route.query),
  ])

  if (wishlistRequest.status === 'rejected') {
    $errorManager({ e: wishlistRequest.reason, name: YSC_API_WISHLIST_ERROR })
  }

  if (camperRequest.status === 'rejected') {
    $errorManager({ e: camperRequest.reason, name: YSC_API_SEARCH_ERROR })
  }

  return true
})

watch(renderMap, (value) => {
  if (!value) {
    mapHiddenByUser.value = true
  }
})

const onQueryUpdated = useDebounceFn(async () => {
  if (route.query.where && !mapHiddenByUser.value) {
    renderMap.value = true
  }

  await executeSearch()

  scrollToTop()
}, 500)

watch(() => route.query, () => onQueryUpdated())

const onProductMouseOver = (id: number) => {
  hoveredItemId.value = id
}

const onProductMouseLeave = () => {
  hoveredItemId.value = null
}

const onToggleMap = () => {
  showMap.value = !showMap.value
  renderMap.value = showMap.value
  if (showMap.value) {
    mapKey.value++
  }
}

const onMapChange = (val: { radius: number, center: { lat: number, lng: number } }) => {
  formMap.value = {
    radius: Math.floor(val.radius),
    latitude: val.center.lat,
    longitude: val.center.lng,
  }
}

const showDiscount = computed(() => !route.query.date_from && !route.query.date_to)
</script>

<template>
  <div class="relative h-full">
    <div
      v-if="loading"
      class="fixed z-[70] h-full w-full bg-white/50"
    >
      <AppLoading class="absolute left-1/2 top-[30vh] -translate-x-1/2 rounded-lg bg-white p-4" />
    </div>

    <div
      class="relative grid h-full grid-rows-[auto_1fr] lg:grid-cols-2 lg:grid-rows-1"
      :class="{ 'pointer-events-none': shouldBlockInterface }"
    >
      <div
        v-if="results.length"
        ref="resultsElement"
        class="relative h-full lg:col-start-1"
        :class="[renderMap ? 'lg:col-span-1' : 'lg:col-span-full']"
      >
        <div class="px-4 pb-4 md:px-6 lg:px-8 md:py-4">
          <h1 class="font-semibold">
            {{ vehicleTypesForTitle }}
          </h1>
          <span class="block text-gray-500 first-letter:uppercase">
            <template v-if="total > 1000">
              {{ formatMoreThan({ count: total, max: 1000 }) }} {{ $t('components.app_search.available_products_more_than') }}
            </template>
            <template v-else>
              {{ $t('components.app_search.available_products_count_plural', { count_number: $n(total) }, total) }}
            </template>
          </span>
        </div>

        <div
          v-if="isCrossedResults"
          class="flex justify-between rounded border border-gray-200 bg-gray-50 text-sm text-gray-500 pl-4 pr-6 py-3 mx-8 mb-6"
        >
          {{ $t('pages.s.goboony_crossed_search_desc') }}
          <img
            src="@/assets/img/supersizing/goboony_full_text.svg"
            class="w-[3.75rem]"
            aria-hidden="true"
          >
        </div>

        <div
          class="grid px-4 pb-4 sm:grid-cols-2 md:px-6 lg:px-8"
          :class="[renderMap ? 'gap-8 sm:!grid-cols-1 lg:w-full xl:!grid-cols-2' : 'gap-8 lg:grid-cols-3 xl:grid-cols-4']"
        >
          <AppSearchResultsCamperCard
            v-for="camper in results"
            :key="camper.id"
            :camper="camper"
            :selected-date-from="filters.date_from"
            :selected-date-to="filters.date_to"
            :is-in-wishlist="isCamperInWishlist(camper.id)"
            :show-wishlist-button="isLoggedIn"
            :show-discount="showDiscount"
            @wishlist-click="$emit('wishlist', $event)"
            @mouseenter="onProductMouseOver(camper.id)"
            @mouseleave="onProductMouseLeave"
          />

          <div
            v-if="total > pageSize"
            class="col-span-full pb-16 lg:pb-0"
          >
            <YscPagination
              :page-size="pageSize"
              :count="total"
            />
          </div>
        </div>
      </div>
      <div
        v-else-if="(!results || results.length === 0)"
        class="px-4 py-16 text-center md:px-6 lg:px-8"
        :class="[renderMap ? 'lg:col-span-1' : 'lg:col-span-full']"
      >
        <img
          src="~assets/img/vehicles/van.svg"
          class="mx-auto w-60 pb-8"
          :alt="$t('components.app_search.no_results.title')"
        >
        <h2 class="text-2xl font-semibold">
          {{ $t('components.app_search.no_results.title') }}
        </h2>
        <p>{{ $t('components.app_search.no_results.description') }}</p>
        <p class="font-semibold">
          {{ $t('components.app_search.no_results.tip') }}
        </p>
        <button
          class="btn btn-secondary mt-8 block mx-auto"
          @click="resetFilters"
        >
          {{ $t('components.app_search.no_results.cta') }}
        </button>
        <button
          v-if="filters.radius"
          class="btn btn-tertiary mt-4 block mx-auto"
          @click="increaseSearchRadius"
        >
          {{ $t('components.app_search.no_results.increase_search_radius_cta') }}
        </button>
      </div>

      <div
        v-if="renderMap"
        class="search-map-container sticky hidden bg-gray-100 row-start-1 lg:row-start-auto lg:col-start-2 lg:block z-50 lg:z-0"
        :class="{ '!block': showMap }"
      >
        <AppSearchResultsMap
          :key="mapKey"
          class="h-full"
          :campers="results"
          :date-from="filters.date_from ?? undefined"
          :date-to="filters.date_to ?? undefined"
          @map-change="onMapChange"
          @marker-click="onMarkerClick"
          @search-in-area="searchInArea"
          @toggle-map="onToggleMap"
          @wishlist="$emit('wishlist', $event.id)"
        />
      </div>
    </div>

    <div
      v-if="!markedItemId"
      class="fixed bottom-0 left-1/2 z-[30] mb-4 -translate-x-1/2 lg:hidden"
    >
      <AppSearchMapToggleButton @toggle="onToggleMap">
        <template v-if="showMap">
          <YscIconsList class="mr-1 inline h-6 w-6" /> {{ $t('components.app_search.display_list') }}
        </template>
        <template v-else>
          <YscIconsMarker class="mr-1 inline h-5 w-5" /> {{ $t('components.app_search.map_toggle') }}
        </template>
      </AppSearchMapToggleButton>
    </div>
  </div>
</template>

<style scoped>
/*
  132px is app header height + filters height
 */
.search-map-container {
  height: calc(100vh - 132px - 1rem + 0.625rem);
  top: calc(158px - 1rem + 1px);
}

/*
  90px is app header height
  74px is filters height
 */
@media screen(md) {
  .search-map-container {
    height: calc(100vh - 90px - 74px);
    top: calc(90px + 74px);
  }
}

/*
  84px is app header height
  74px is filters height
 */
@media screen(lg) {
  .search-map-container {
    height: calc(100vh - 84px - 74px);
    top: calc(84px + 74px);
  }
}
</style>
